import type { Client as NextClient, Config as NextConfig } from '@common/clients/api/generated/client';
import { type Client, type Config } from '@common/clients/api-cron/generated/client';
import { type PartialUserContext } from '@common/defaults/UserContext';
import { logger } from '@common/logger';
import { updateStatus } from '@common/supertokens/updateStatus';
import { Duration } from '@common/types/Duration';

import { checkAuthStatus } from './utils/checkAuthStatus';
import { getErrorInterceptor } from './utils/getErrorInterceptor';
import { getFetchWithErrorLogging } from './utils/getFetchWithErrorLogging';
import { getFetchWithTimeout } from './utils/getFetchWithTimeout';
import { getRuntimes } from './utils/getRuntimes';
import { logRequestToNewRelic } from './utils/logRequestToNewRelic';
import { shouldSkipStorybook } from './utils/shouldSkipStorybook';
import type { ApiFetchOptions } from './types';

const DEFAULT_TIMEOUT = Number(process.env.NEXT_PUBLIC_API_TIMEOUT) || 5 * Duration.second;

export type BaseClientOptions = {
    useNextClient: boolean;
    useNewRelic?: boolean;
    useStatusCheck?: boolean;
};

export interface ClientOptions {
    userContext?: PartialUserContext;
    fetchOptions?: Config & ApiFetchOptions;
}

export interface ClientOptionsNext extends Omit<ClientOptions, 'fetchOptions'> {
    fetchOptions?: NextConfig & ApiFetchOptions;
}

/**
 * @description Create a base client for fetch or nextjs
 * Depending on the baseClientOptions.useNextClient property,
 * the client will be a nextjs client or a fetch client.
 * Type is dynamically imported to prevent build error in api
 * which doesnt know about the nextjs client.
 * @param baseClientOptions - The base client options
 * @param clientOptions - The client options
 * @returns A client
 */
function createBaseClient<T extends boolean>(
    baseClientOptions: BaseClientOptions & { useNextClient: T },
    clientOptions: T extends true ? ClientOptionsNext : ClientOptions,
): T extends true ? NextClient : Client;

function createBaseClient(
    baseClientOptions: BaseClientOptions,
    clientOptions: ClientOptions | ClientOptionsNext,
) {
    const { useNextClient = false, useNewRelic = false, useStatusCheck = false } = baseClientOptions;

    const { userContext, fetchOptions = {} } = clientOptions;

    const { isEdgeRuntime } = getRuntimes();

    // Annotate to prevent interceptor type error
    // the actual return type is dictated by the useNextClient flag
    let base: Client;

    // Set fetch
    fetchOptions.fetch = fetchOptions.fetch ?? fetch;

    // Add timeout
    fetchOptions.fetch = getFetchWithTimeout(fetchOptions.fetch, fetchOptions.timeout || DEFAULT_TIMEOUT);

    // Add non-HTTP error logging
    fetchOptions.fetch = getFetchWithErrorLogging(fetchOptions.fetch);

    // Edge is used in the nextjs middleware as runtime and doesnt support
    // require statements. If needed, we can switch to import('@hey-api/client-next') in the future,
    if (useNextClient && !isEdgeRuntime) {
        try {
            const { createClient: createClientNext } = require('@common/clients/api/generated/client');
            base = createClientNext(fetchOptions);
        } catch (error) {
            logger.warn('Error importing @hey-api/client-next:', error);
            const { createClient } = require('@common/clients/api-cron/generated/client');
            base = createClient(fetchOptions as Config);
        }
    } else {
        const { createClient } = require('@common/clients/api-cron/generated/client');
        base = createClient(fetchOptions as Config);
    }

    const requestInterceptor = async (request: Request) => {
        if (shouldSkipStorybook('Api calls do not work from storybook')) {
            return request;
        }

        if (useStatusCheck) {
            try {
                await checkAuthStatus(userContext);
            } catch (error) {
                if (
                    typeof error === 'object' &&
                    error !== null &&
                    'status' in error &&
                    typeof error.status === 'number'
                ) {
                    updateStatus(userContext, error.status);
                }

                throw error;
            }
        }

        if (useNewRelic) {
            logRequestToNewRelic(request.url, request);
        }

        return request;
    };

    base.interceptors.request.use(requestInterceptor);

    base.interceptors.response.use((response) => {
        if ('status' in response && response.status === 401) {
            updateStatus(userContext, response.status);
        }

        return response;
    });

    base.interceptors.error.use(getErrorInterceptor(useNewRelic));

    return base;
}

export { createBaseClient };
